import { z } from 'zod';
import {
  ARCHIVED_P,
  LOG_ARCHIVED_PREFIX,
  type Change,
  type Contact,
  type ContactPatch,
  type ExtraValue,
  type LogEntry,
  type LogType,
  type MessageOrigin,
  type WritableContactField,
} from './types.js';
import { CONTACT_FIELDS, fieldByKey, notionName } from './schema.js';

/**
 * Actions, primitives and inverses (§5.5, D-6). Written once here and used by both
 * ends: the browser applies primitives to its replica optimistically, the server
 * applies them to Notion with `from` values read from its own replica.
 */

/* ---------- primitives ---------- */

export type Primitive =
  | { kind: 'set'; page: string; p: string; from: ExtraValue; to: ExtraValue }
  | { kind: 'create'; page: string; fields: ContactPatch }
  | { kind: 'archive'; page: string }
  | { kind: 'restore'; page: string }
  | { kind: 'archiveLog'; entry: string }
  | { kind: 'restoreLog'; entry: string };

/** The inverse of one primitive (§5.5 table). `create` inverts to `archive`. */
export function invertPrimitive(p: Primitive): Primitive {
  switch (p.kind) {
    case 'set':
      return { kind: 'set', page: p.page, p: p.p, from: p.to, to: p.from };
    case 'create':
      return { kind: 'archive', page: p.page };
    case 'archive':
      return { kind: 'restore', page: p.page };
    case 'restore':
      return { kind: 'archive', page: p.page };
    case 'archiveLog':
      return { kind: 'restoreLog', entry: p.entry };
    case 'restoreLog':
      return { kind: 'archiveLog', entry: p.entry };
  }
}

/** Inverse of a list: reversed order, each primitive inverted. */
export function invert(ps: Primitive[]): Primitive[] {
  return [...ps].reverse().map(invertPrimitive);
}

/** `Changes` JSON for the log entry (§4.3) that records these primitives. */
export function changesOf(ps: Primitive[]): Change[] {
  const out: Change[] = [];
  for (const p of ps) {
    switch (p.kind) {
      case 'set':
        out.push({ p: p.p, from: p.from, to: p.to });
        break;
      case 'create':
        for (const [key, value] of Object.entries(p.fields) as [WritableContactField, ExtraValue][]) {
          out.push({ p: notionName(key), from: null, to: value });
        }
        break;
      case 'archive':
        out.push({ p: ARCHIVED_P, from: false, to: true });
        break;
      case 'restore':
        out.push({ p: ARCHIVED_P, from: true, to: false });
        break;
      case 'archiveLog':
        out.push({ p: LOG_ARCHIVED_PREFIX + p.entry, from: false, to: true });
        break;
      case 'restoreLog':
        out.push({ p: LOG_ARCHIVED_PREFIX + p.entry, from: true, to: false });
        break;
    }
  }
  return out;
}

/** Rebuild primitives from a stored entry's `Changes` (the entry's page is the contact). */
export function primitivesOfChanges(changes: Change[], page: string): Primitive[] {
  return changes.map((c): Primitive => {
    if (c.p === ARCHIVED_P) return c.to ? { kind: 'archive', page } : { kind: 'restore', page };
    if (c.p.startsWith(LOG_ARCHIVED_PREFIX)) {
      const entry = c.p.slice(LOG_ARCHIVED_PREFIX.length);
      return c.to ? { kind: 'archiveLog', entry } : { kind: 'restoreLog', entry };
    }
    return { kind: 'set', page, p: c.p, from: c.from, to: c.to };
  });
}

/**
 * The primitives that undo a logged entry (§5.5 `revert`). Notes and suggestions are
 * archived; a `create` archives the page (Notion keeps it in Trash for 30 days);
 * everything else, including `external` and `revert` entries, inverts its `Changes`.
 */
export function inverseOfEntry(e: LogEntry): Primitive[] {
  if (e.type === 'note' || e.type === 'suggestion') return [{ kind: 'archiveLog', entry: e.id }];
  if (!e.contactId) throw new Error('entry has no contact');
  if (e.type === 'create') return [{ kind: 'archive', page: e.contactId }];
  return invert(primitivesOfChanges(e.changes, e.contactId));
}

/** An entry can be reverted only when every change touches a field the app may write (D-4). */
export function isRevertable(e: Pick<LogEntry, 'type' | 'changes' | 'reverted' | 'archived' | 'contactId'>): boolean {
  if (e.reverted || e.archived || !e.contactId) return false;
  if (e.type === 'note' || e.type === 'suggestion' || e.type === 'create') return true;
  if (!e.changes.length) return false;
  return e.changes.every((c) => c.p === ARCHIVED_P || c.p.startsWith(LOG_ARCHIVED_PREFIX) || fieldKeyOf(c.p) !== null);
}

/* ---------- applying primitives to a canonical contact (replica side) ---------- */

const KEY_BY_NOTION = new Map(CONTACT_FIELDS.map((f) => [f.notion, f.key] as const));

export function fieldKeyOf(p: string): WritableContactField | null {
  const key = KEY_BY_NOTION.get(p);
  if (!key) return null;
  const f = fieldByKey(key);
  return f?.writable ? (key as WritableContactField) : null;
}

/** Current value of a Notion property on a canonical contact, as the log would record it. */
export function valueOf(c: Contact, p: string): ExtraValue {
  const key = fieldKeyOf(p);
  if (!key) return c.extra[p] ?? null;
  const v = c[key];
  return (v as ExtraValue) ?? null;
}

/** Apply a `set` to a contact. Owner takes a string[]; Next Action replaces the whole list (D-12). */
export function withValue(c: Contact, p: string, value: ExtraValue): Contact {
  const key = fieldKeyOf(p);
  if (!key) throw new Error(`${p} is not a writable field`);
  const next: Contact = { ...c };
  switch (key) {
    case 'owner':
      next.owner = Array.isArray(value) ? value : value ? [String(value)] : [];
      break;
    case 'nextAction':
      next.nextAction = value == null || value === '' ? null : String(value);
      next.nextActionExtra = [];
      break;
    case 'companyName':
    case 'contact':
      next[key] = value == null ? '' : String(value);
      break;
    default:
      next[key] = value == null || value === '' ? null : String(value);
  }
  return next;
}

/** Apply a `create` to produce a canonical contact for the replica (ids and times from the caller). */
export function contactFromCreate(page: string, fields: ContactPatch, now: string): Contact {
  const base: Contact = {
    id: page,
    companyName: '',
    contact: '',
    contactRole: null,
    email: null,
    phone: null,
    linkedin: null,
    companySite: null,
    stage: null,
    status: null,
    nextAction: null,
    nextActionExtra: [],
    owner: [],
    nextContactDate: null,
    lastContactDate: null,
    leadSource: null,
    industry: null,
    description: '',
    createdTime: now,
    lastEditedTime: now,
    lastEditedBy: null,
    url: '',
    archived: false,
    extra: {},
  };
  let c = base;
  for (const [key, value] of Object.entries(fields) as [WritableContactField, ExtraValue][]) {
    c = withValue(c, notionName(key), value);
  }
  return c;
}

/** Apply primitives to an in-memory replica (contacts + log). Used by the browser and by tests. */
export interface Replica {
  contacts: Map<string, Contact>;
  log: Map<string, LogEntry>;
}

export function applyPrimitives(r: Replica, ps: Primitive[], now: string): void {
  for (const p of ps) {
    switch (p.kind) {
      case 'set': {
        const c = r.contacts.get(p.page);
        if (!c) throw new Error(`unknown contact ${p.page}`);
        r.contacts.set(p.page, { ...withValue(c, p.p, p.to), lastEditedTime: now });
        break;
      }
      case 'create':
        r.contacts.set(p.page, contactFromCreate(p.page, p.fields, now));
        break;
      case 'archive':
      case 'restore': {
        const c = r.contacts.get(p.page);
        if (!c) throw new Error(`unknown contact ${p.page}`);
        r.contacts.set(p.page, { ...c, archived: p.kind === 'archive', lastEditedTime: now });
        break;
      }
      case 'archiveLog':
      case 'restoreLog': {
        const e = r.log.get(p.entry);
        if (!e) throw new Error(`unknown log entry ${p.entry}`);
        r.log.set(p.entry, { ...e, archived: p.kind === 'archiveLog' });
        break;
      }
    }
  }
}

/* ---------- user actions ---------- */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
const id = z.string().min(1);
const WRITABLE: [WritableContactField, ...WritableContactField[]] = [
  'companyName', 'contact', 'contactRole', 'email', 'phone', 'linkedin', 'companySite', 'stage', 'status',
  'nextAction', 'owner', 'nextContactDate', 'lastContactDate', 'leadSource', 'industry',
];
const extraValue = z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]);

export const ActionPayloads = {
  createProspect: z.object({
    tempId: id,
    name: z.string().trim().min(1),
    organization: z.string().trim().default(''),
    notes: z.string().trim().min(1),
    leadSource: z.string().nullable().default(null),
    nextContactDate: isoDate.nullable(),
    /** Accepted AI proposals (§9); `nextAction` overrides the First Contact default. */
    contactRole: z.string().nullable().optional(),
    industry: z.string().nullable().optional(),
    nextAction: z.string().nullable().optional(),
    /** Select values outside Notion's option list that the user explicitly confirmed (D-14); logged. */
    newOptions: z.array(z.object({ property: z.string(), value: z.string() })).default([]),
  }),
  updateProspect: z.object({
    contactId: id,
    notes: z.string().trim().min(1),
    /** Omitted fields are left as they are. */
    name: z.string().trim().optional(),
    organization: z.string().trim().optional(),
    nextContactDate: isoDate.nullable().optional(),
    contactRole: z.string().nullable().optional(),
    industry: z.string().nullable().optional(),
    nextAction: z.string().nullable().optional(),
    newOptions: z.array(z.object({ property: z.string(), value: z.string() })).default([]),
  }),
  /** The AI's suggested message for a contact (§9): a `suggestion` entry, latest one shown. */
  addSuggestion: z.object({ contactId: id, text: z.string().trim().min(1).max(4000) }),
  setField: z.object({
    contactId: id,
    field: z.enum(WRITABLE),
    value: extraValue,
  }),
  addNote: z.object({ contactId: id, text: z.string().trim().min(1) }),
  followedUp: z.object({
    contactId: id,
    nextContactDate: isoDate.nullable(),
    nextAction: z.string().nullable().optional(),
    note: z.string().trim().optional(),
  }),
  /** A message was sent to the contact (§9): logged with its text and origin, dates as for followedUp. */
  sentMessage: z.object({
    contactId: id,
    text: z.string().trim().min(1).max(4000),
    origin: z.enum(['ai', 'ai-edited', 'manual']),
    nextContactDate: isoDate.nullable(),
    nextAction: z.string().nullable().optional(),
  }),
  snooze: z.object({ contactId: id, nextContactDate: isoDate }),
  archiveProspect: z.object({ contactId: id }),
  revert: z.object({ entryId: id }),
} as const;

export type ActionType = keyof typeof ActionPayloads;
export type ActionPayload<T extends ActionType = ActionType> = z.infer<(typeof ActionPayloads)[T]>;

export const ActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('createProspect'), payload: ActionPayloads.createProspect }),
  z.object({ type: z.literal('updateProspect'), payload: ActionPayloads.updateProspect }),
  z.object({ type: z.literal('setField'), payload: ActionPayloads.setField }),
  z.object({ type: z.literal('addNote'), payload: ActionPayloads.addNote }),
  z.object({ type: z.literal('addSuggestion'), payload: ActionPayloads.addSuggestion }),
  z.object({ type: z.literal('followedUp'), payload: ActionPayloads.followedUp }),
  z.object({ type: z.literal('sentMessage'), payload: ActionPayloads.sentMessage }),
  z.object({ type: z.literal('snooze'), payload: ActionPayloads.snooze }),
  z.object({ type: z.literal('archiveProspect'), payload: ActionPayloads.archiveProspect }),
  z.object({ type: z.literal('revert'), payload: ActionPayloads.revert }),
]);

export const ActionEnvelopeSchema = z.object({
  /** Client ULID; idempotency key (§5.4). */
  actionId: z.string().min(10).max(64),
  /** Client timestamp (§5.7). */
  at: z.string().datetime({ offset: true }),
  /** The client's before-values per Notion property, for conflict flagging (§5.5). */
  expected: z.record(z.string(), extraValue).default({}),
});

export type Action = z.infer<typeof ActionSchema> & z.infer<typeof ActionEnvelopeSchema>;

export function parseAction(raw: unknown): Action {
  const env = ActionEnvelopeSchema.parse(raw);
  const body = ActionSchema.parse(raw);
  return { ...env, ...body } as Action;
}

/* ---------- expansion ---------- */

export interface ExpandContext {
  /** Replica lookup; the contact the action targets (null for createProspect). */
  contact: Contact | null;
  /** For `revert`: the entry being reverted, and its contact (as `contact`). */
  entry?: LogEntry | null;
  today: string;
  me: { notionUserId: string | null };
}

export interface Expansion {
  primitives: Primitive[];
  /** The one CRM Log entry this action writes. */
  entry: { type: LogType; text: string; contactId: string; reverts: string | null; origin?: MessageOrigin };
}

export const CREATE_DEFAULTS = { status: 'Not started', stage: 'Lead', nextAction: 'First Contact' } as const;

/** Expand a user action into primitives (§5.5 table) against the given state. */
export function expandAction(a: Action, ctx: ExpandContext): Expansion {
  const c = ctx.contact;
  const need = (): Contact => {
    if (!c) throw new ActionError('contact not found', 404);
    if (c.archived && a.type !== 'revert') throw new ActionError('contact is archived', 409);
    return c;
  };
  const set = (contact: Contact, field: WritableContactField, to: ExtraValue): Primitive | null => {
    const p = notionName(field);
    const from = valueOf(contact, p);
    if (JSON.stringify(from ?? null) === JSON.stringify(to ?? null)) return null;
    return { kind: 'set', page: contact.id, p, from, to };
  };
  const sets = (contact: Contact, pairs: [WritableContactField, ExtraValue][]): Primitive[] =>
    pairs.map(([f, v]) => set(contact, f, v)).filter((x): x is Primitive => x !== null);

  switch (a.type) {
    case 'createProspect': {
      const p = a.payload;
      const fields: ContactPatch = {
        companyName: p.organization || p.name,
        contact: p.name,
        status: CREATE_DEFAULTS.status,
        stage: CREATE_DEFAULTS.stage,
        nextAction: p.nextAction ?? CREATE_DEFAULTS.nextAction,
        owner: ctx.me.notionUserId ? [ctx.me.notionUserId] : [],
        nextContactDate: p.nextContactDate,
        leadSource: p.leadSource,
      };
      if (p.contactRole) fields.contactRole = p.contactRole;
      if (p.industry) fields.industry = p.industry;
      return {
        primitives: [{ kind: 'create', page: p.tempId, fields }],
        entry: { type: 'create', text: withNewOptions(p.notes, p.newOptions), contactId: p.tempId, reverts: null },
      };
    }
    case 'updateProspect': {
      const contact = need();
      const p = a.payload;
      const pairs: [WritableContactField, ExtraValue][] = [];
      if (p.name !== undefined && p.name) pairs.push(['contact', p.name]);
      if (p.organization !== undefined && p.organization) pairs.push(['companyName', p.organization]);
      if (p.nextContactDate !== undefined) pairs.push(['nextContactDate', p.nextContactDate]);
      if (p.contactRole) pairs.push(['contactRole', p.contactRole]);
      if (p.industry) pairs.push(['industry', p.industry]);
      if (p.nextAction) pairs.push(['nextAction', p.nextAction]);
      return { primitives: sets(contact, pairs), entry: { type: 'update', text: withNewOptions(p.notes, p.newOptions), contactId: contact.id, reverts: null } };
    }
    case 'addSuggestion': {
      const contact = need();
      return { primitives: [], entry: { type: 'suggestion', text: a.payload.text, contactId: contact.id, reverts: null } };
    }
    case 'setField': {
      const contact = need();
      const s = set(contact, a.payload.field, a.payload.value);
      return { primitives: s ? [s] : [], entry: { type: 'update', text: '', contactId: contact.id, reverts: null } };
    }
    case 'addNote': {
      const contact = need();
      return { primitives: [], entry: { type: 'note', text: a.payload.text, contactId: contact.id, reverts: null } };
    }
    case 'followedUp': {
      const contact = need();
      const p = a.payload;
      const pairs: [WritableContactField, ExtraValue][] = [
        ['lastContactDate', ctx.today],
        ['nextContactDate', p.nextContactDate],
      ];
      if (p.nextAction !== undefined) pairs.push(['nextAction', p.nextAction]);
      return { primitives: sets(contact, pairs), entry: { type: 'follow-up', text: p.note ?? '', contactId: contact.id, reverts: null } };
    }
    case 'sentMessage': {
      const contact = need();
      const p = a.payload;
      const pairs: [WritableContactField, ExtraValue][] = [
        ['lastContactDate', ctx.today],
        ['nextContactDate', p.nextContactDate],
      ];
      if (p.nextAction !== undefined) pairs.push(['nextAction', p.nextAction]);
      return { primitives: sets(contact, pairs), entry: { type: 'message', text: p.text, contactId: contact.id, reverts: null, origin: p.origin } };
    }
    case 'snooze': {
      const contact = need();
      return {
        primitives: sets(contact, [['nextContactDate', a.payload.nextContactDate]]),
        entry: { type: 'snooze', text: '', contactId: contact.id, reverts: null },
      };
    }
    case 'archiveProspect': {
      const contact = need();
      return { primitives: [{ kind: 'archive', page: contact.id }], entry: { type: 'archive', text: '', contactId: contact.id, reverts: null } };
    }
    case 'revert': {
      const e = ctx.entry;
      if (!e) throw new ActionError('log entry not found', 404);
      if (e.reverted) throw new ActionError('entry already reverted', 409);
      if (e.archived) throw new ActionError('entry is archived', 409);
      if (!e.contactId) throw new ActionError('entry has no contact', 409);
      const prims = inverseOfEntry(e);
      // Re-read `from` for sets so the log shows the true previous state (D-6).
      const fresh = prims.map((p) => (p.kind === 'set' && c ? { ...p, from: valueOf(c, p.p) } : p));
      return { primitives: fresh, entry: { type: 'revert', text: '', contactId: e.contactId, reverts: e.id } };
    }
  }
}

/** D-14: a confirmed new select option is recorded in the entry's text so the decision is auditable. */
function withNewOptions(text: string, newOptions: { property: string; value: string }[]): string {
  if (!newOptions.length) return text;
  return `${text}\n\n${newOptions.map((o) => `[New ${o.property} option "${o.value}" confirmed by the user]`).join('\n')}`;
}

/** The client's before-values keyed by Notion property, sent as `expected` (§5.5). */
export function expectedOf(ps: Primitive[]): Record<string, ExtraValue> {
  const out: Record<string, ExtraValue> = {};
  for (const p of ps) if (p.kind === 'set') out[p.p] = p.from;
  return out;
}

/** True when any set's server-side `from` differs from what the client expected. */
export function hasConflict(ps: Primitive[], expected: Record<string, ExtraValue>): boolean {
  for (const p of ps) {
    if (p.kind !== 'set' || !(p.p in expected)) continue;
    if (JSON.stringify(expected[p.p] ?? null) !== JSON.stringify(p.from ?? null)) return true;
  }
  return false;
}

export class ActionError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

/* ---------- summaries (§6.4 "what") ---------- */

export function formatValue(v: ExtraValue): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v);
}

export interface ChangeLabels {
  archived: string;
  restored: string;
  noteRemoved: string;
  noteRestored: string;
}
const EN_LABELS: ChangeLabels = { archived: 'Archived', restored: 'Restored', noteRemoved: 'Note removed', noteRestored: 'Note restored' };

/** One line per change, e.g. "Stage: Lead → Qualified". Owner ids are resolved by the caller; labels can be localized. */
export function describeChanges(changes: Change[], personName: (id: string) => string = (id) => id, labels: ChangeLabels = EN_LABELS): string[] {
  return changes.map((ch) => {
    if (ch.p === ARCHIVED_P) return ch.to ? labels.archived : labels.restored;
    if (ch.p.startsWith(LOG_ARCHIVED_PREFIX)) return ch.to ? labels.noteRemoved : labels.noteRestored;
    const fmt = (v: ExtraValue) => (ch.p === 'Owner' && Array.isArray(v) ? formatValue(v.map(personName)) : formatValue(v));
    return `${ch.p}: ${fmt(ch.from)} → ${fmt(ch.to)}`;
  });
}

const TYPE_LABEL: Record<LogType, string> = {
  note: 'Note',
  create: 'Created',
  update: 'Updated',
  'follow-up': 'Followed up',
  snooze: 'Snoozed',
  archive: 'Archived',
  restore: 'Restored',
  suggestion: 'Suggested message',
  external: 'Edited in Notion',
  revert: 'Reverted',
  message: 'Message sent',
};

export function typeLabel(t: LogType): string {
  return TYPE_LABEL[t];
}

/** Short summary for the entry's `Name` and the Log view's "what" column. */
export function summarize(e: Pick<LogEntry, 'type' | 'text' | 'changes'>, personName?: (id: string) => string): string {
  const lines = describeChanges(e.changes, personName);
  if (e.type === 'note' || e.type === 'suggestion' || e.type === 'message') return e.text.length > 80 ? e.text.slice(0, 77) + '…' : e.text;
  if (e.type === 'create') return 'Created';
  if (lines.length) return lines.join(' · ');
  return TYPE_LABEL[e.type];
}
